import { useCallback, useEffect } from "react";
import { apiGet } from "@/services/genesisApi/httpsMethods";
import type { ApiResponse } from "@/services/genesisApi/httpsMethods";
import type { Deal } from "@/types/deal";

type UsePipelineDealsArgs = {
  setDeals: (value: Deal[] | ((prev: Deal[]) => Deal[])) => void;
  setInitialDeals: (deals: Deal[]) => void;
  activeOnly: boolean;
  setActiveOnly: (activeOnly: boolean) => void;
  showErrorFromResponse: (response: ApiResponse) => void;
};

/**
 * Loads the Pipeline rows. Cells save individually (usePipelineCellSave), so
 * there is no edit lock or draft: `deals` only differs from `initialDeals`
 * while a cell save is in flight.
 */
export function usePipelineDeals({
  setDeals,
  setInitialDeals,
  activeOnly,
  setActiveOnly,
  showErrorFromResponse,
}: UsePipelineDealsArgs) {
  const getAllDeals = useCallback(
    (activeBoolean: boolean) => {
      apiGet(`deals?active_only=${activeBoolean}`)
        .then((response) => {
          if (response.success && response.data) {
            setInitialDeals(structuredClone(response.data));
            setDeals(response.data);
            setActiveOnly(activeBoolean);
          } else {
            showErrorFromResponse(response);
          }
        })
        .catch(() => {
          showErrorFromResponse({
            success: false,
            status: 500,
            message: "Failed to fetch deals. Please try again.",
          });
        });
    },
    [setActiveOnly, setDeals, setInitialDeals, showErrorFromResponse],
  );

  useEffect(() => {
    getAllDeals(activeOnly);
    // Intentionally mount-only, matching prior Pipeline behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { getAllDeals };
}
